import { useEffect, useMemo, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { updateMachine, getMachineBrand, getMachineConnectionMethod, getMachines, selectMachines } from '@reducers/machines';
import { getStateParams } from '@helpers';
import { formatBrandList, formatConnectionMethods, buildSavePayload, FormattedListItem, FormattedConnectionMethods } from '@components/settings/v2/structure/machines/machineConnection/helpers';

const CONNECTION_OPTIONS = [
  { name: 'Automatic', value: true, key: 'Automatic' },
  { name: 'Manual', value: false, key: 'Manual' },
];

const BRAND_WITH_CONNECTION_METHOD_CODE = ['other', 'heidenhain'];

const getValue = <T,>(overridden: T | undefined, original: T): T => (overridden === undefined ? original : overridden);

interface MachineConnectionFormData {
  connectionMethodIsAutomatic?: boolean;
  machineBrandCode?: string;
  connectionMethodCode?: string;
  defaultPort?: number;
  allowNcFileTransfer?: boolean;
}

export const useManageMachineConnection = () => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const machines = useSelector(selectMachines);

  const id = Number(getStateParams(route).id);
  const machine: Record<string, any> = useMemo(
    () => machines.find((m: any) => m.machineId === id) || {},
    [machines, id]
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MachineConnectionFormData>({});
  const [connectionMethod, setConnectionMethod] = useState<FormattedConnectionMethods | null>(null);
  const [machineBrandList, setMachineBrandList] = useState<FormattedListItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [, brand, connection] = await Promise.all([
        dispatch(getMachines()),
        dispatch(getMachineBrand()),
        dispatch(getMachineConnectionMethod()),
      ]);

      if (cancelled) return;

      if (brand) {
        setMachineBrandList(formatBrandList(brand as any[]));
      }
      if (connection) {
        setConnectionMethod(formatConnectionMethods(connection as any[]));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const { machineConnection = {}, machineBrand = {}, machineId, machineControl } = machine;

  const connectionMethodIsAutomatic = getValue(data.connectionMethodIsAutomatic, machineConnection?.connectionMethodIsAutomatic);
  const machineBrandCode = getValue(data.machineBrandCode, machineBrand?.code);
  const connectionMethodCode = getValue(data.connectionMethodCode, machineConnection?.connectionMethod?.code);
  const defaultPort = getValue(data.defaultPort, machineConnection?.connectionMethod?.defaultPort);
  const allowNcFileTransfer = getValue(data.allowNcFileTransfer, machineConnection?.allowNcFileTransfer);

  const updateData = (key: string, value: unknown) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const payload = buildSavePayload({
        machineId,
        fullName: machine.fullName,
        shortName: machine.shortName,
        machineBrandCode,
        machineControl,
        machineConnection,
        connectionMethodCode,
        defaultPort,
        allowNcFileTransfer,
        connectionMethodIsAutomatic,
      });

      await dispatch(updateMachine({ machine: payload }, payload));
    } finally {
      setLoading(false);
    }
  };

  const showBrandList = machineBrandList !== null && !connectionMethodIsAutomatic;
  const showProtocolList =
    connectionMethod !== null &&
    !!machineBrandCode &&
    !connectionMethodIsAutomatic &&
    BRAND_WITH_CONNECTION_METHOD_CODE.includes(machineBrandCode);

  return {
    loading,
    connectionOptions: CONNECTION_OPTIONS,
    connectionMethodIsAutomatic,
    machineBrandCode,
    connectionMethodCode,
    allowNcFileTransfer,
    machineBrandList,
    connectionMethod,
    showBrandList,
    showProtocolList,
    updateData,
    save,
  };
};
